import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) {
      nav(r.user.role === "admin" ? "/admin" : "/dashboard");
    } else {
      setError(r.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16 relative grain">
      <div className="max-w-md w-full">
        <Link to="/" className="flex items-center gap-3 justify-center mb-10" data-testid="login-logo-link">
          <img src={LOGO_URL} alt="" className="h-16 w-16 object-contain drop-shadow-[0_0_12px_rgba(0,229,255,0.5)]" />
          <div className="leading-tight">
            <div className="font-display text-2xl text-chrome">DAZZLIN' DYMES</div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#00E5FF]">Parent Portal</div>
          </div>
        </Link>

        <div className="card-dark p-8">
          <div className="overline mb-2">Welcome back</div>
          <h1 className="font-display text-3xl text-chrome uppercase">Parent Login</h1>

          <form onSubmit={onSubmit} className="mt-8 space-y-6" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Email</label>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-line mt-1"
                placeholder="you@email.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Password</label>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-line mt-1"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div className="text-sm text-red-400 border border-red-400/30 bg-red-400/5 rounded px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} className="btn-electric w-full" data-testid="login-submit-button">
              {loading ? <Loader2 className="animate-spin" size={16} /> : "Sign In"}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-white/50">
            New family?{" "}
            <Link to="/register" className="text-[#00E5FF] hover:text-white" data-testid="login-to-register-link">
              Create an account
            </Link>
          </div>
          <div className="mt-5 text-[11px] text-white/45 leading-relaxed border border-white/8 bg-white/3 rounded-md p-3" data-testid="login-signup-help">
            <div className="font-varsity tracking-[0.2em] text-white/65 uppercase text-[10px] mb-1">First time here?</div>
            Already on the team? Tap <span className="text-white">Create an account</span> above with your email + a password — that gets you into your parent portal where Coach&apos;s messages, the schedule, and tuition info live.
            <br /><br />
            Looking to <span className="text-white">try out</span> for the Dymes? Head to{" "}
            <Link to="/join" className="text-[#00E5FF] hover:text-white">/join</Link> and fill out the short questionnaire — Coach replies within 48 hours.
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="text-xs uppercase tracking-[0.3em] text-white/40 hover:text-white" data-testid="login-back-home">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
